import * as PIXI from "pixi.js";
import { getGameViewArtWidth } from "../../../utils/gameViewArtWidth";
import i18next from "i18next";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import GameViewSimpleController, {
  resolveBoardPlacement,
  getBaseColumnSetup,
  LANDSCAPE_COLUMN_BONES,
  LANDSCAPE_COLUMN_OFFSET_CONFIG,
} from "../GameViewSimple/GameViewSimpleController";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { Settings } from "../../../settings";
import { ULTRA_WIDE } from "../../../Contexts/DimensionsContext";
import { ZONE_THRESHOLDS } from "../../../RGS/Providers/PopiPlay/holdAndWinState";
import TickerTimeout from "../../../utils/TickerTimeout";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { setSpinesDormant } from "../../../Games/Shared/spineDormancy";
import { Physics, Vector2 } from "@esotericsoftware/spine-core";
import { COLLECTOR_BOTTOM } from "../HoldAndWinCounter/HoldAndWinCollectorController";

// The room frame during the last cells' anticipation (playGridAnticipation).
// Its own track: 0 idle, 1-2 logo, 3 anticipation, 4-9 jackpot hits, 10 logo
// reel (GameViewSimpleController).
const GRID_ANTICIPATION_TRACK = 12;
const GRID_ANTICIPATION_IN = "reel_full_in";
const GRID_ANTICIPATION_IDLE = "reel_full_idle";
const GRID_ANTICIPATION_OUT = "reel_full_out";
const GRID_ANTICIPATION_CLIPS = [
  GRID_ANTICIPATION_IN,
  GRID_ANTICIPATION_IDLE,
  GRID_ANTICIPATION_OUT,
];
// The Royal plate's anticipation / win glow (playRoyalAnticipation, playRoyalWin).
const ROYAL_PLATE_TRACK = 13;
// ── The box zooms out for the End Collector (2026-10-14) ── during the
// respins the box is a bit bigger: the room spine's `reels_zoom` bone (under
// `reels`, on the frame's TOP, everything of the box under it) has a setup
// scale of 1.018 — 1.0088 put the respin counter's visible bottom on the gap
// the collector keeps to the toolbar (counter 36 units under its slot,
// collector 46.9); a bit bigger again (2026-10-14: "too far from toolbar").
// `reel_zoom_out` takes it to 0.98 of the layout tables (3.7% in all) — the
// collector a touch above where it was fitted — and holds there. Moved like
// heavy stone (2026-10-14: "not like rubber", then "a bit slow … heavier"):
// a beat of inertia, a hard fall, an impact 0.5% past the end, then ONE
// smooth ease back to rest by 0.72 s (the shudder read as wobble — removed).
// All in the spine: the same ratio on every layout, so one clip.
const REEL_ZOOM = { CLIP: "reel_zoom_out", TRACK: 14, BONE: "reels_zoom" };
// ── The bowls' fire bursts when a row unlocks (PO, 2026-10-15) ── our own
// clip in the room spine (`pot_fire_pulse`, 1.5 s): each bowl's fire grows to
// 1.78 x 2.7 by 0.5 s OUT of the bowl, two side tongues (`pot_burst_a/b_r/_l`,
// screen blend, full strength, up to 1.05 x 2.2) grow out of it and lean ±14°,
// then all settle back in one smooth ease, tongues fading. The fire art's
// bottom 46 rows (its root: a fade
// with a seam) must stay behind the lip, so the bowl fire and tongues are
// 4-row meshes: a deform keyed every 1/30 s holds the root at its rest size
// while the bone scale grows the flame above (pivots `pot_fire_pulse_r/_l` on
// the seam row). Meshes need untrimmed frames: `pot_fire_full_*`, kept
// untrimmed on the shared pages spines/shared/ (the same 30 fire frames the
// logo, transition, reel anticipation, max win and basic win atlases list —
// one download). The bowl lip does not grow; the rest look is unchanged.
const POT_FIRE_PULSE = { CLIP: "pot_fire_pulse", TRACK: 15 };
const ROYAL_ANTICIPATION_IN = "rjp_anticipation_in";
const ROYAL_ANTICIPATION_LOOP = "rjp_anticipation_loop";
const ROYAL_ANTICIPATION_OUT = "rjp_anticipation_out";
const ROYAL_WIN_IN = "rjp_anticipation_win_in";
const ROYAL_WIN_LOOP = "rjp_anticipation_win_loop";
const ROYAL_WIN_OUT = "rjp_anticipation_win_out";
const ROYAL_PLATE_CLIPS = [
  ROYAL_ANTICIPATION_IN,
  ROYAL_ANTICIPATION_LOOP,
  ROYAL_ANTICIPATION_OUT,
  ROYAL_WIN_IN,
  ROYAL_WIN_LOOP,
  ROYAL_WIN_OUT,
];

/**
 * HoldAndWinGameViewController — the Cleopatra's Fire Hold & Win room.
 *
 * hold_and_win_game_view shares the base rig's plumbing (reels bone, layout
 * skins, `reel_landscape` constraint, jackpot column) but not its layout: a
 * tall 5x8 frame (`nhw_reel_p`), and the respin counter and the collector are
 * BUILT INTO this spine (counter_* / collector_* clips, `txt_slot_collector`).
 * So, like FreeSpinsGameViewController, this only points GameViewSimple-
 * Controller at the other spine and at its own registry keys.
 *
 * Its idle is `hnw_bg_idle`. The logo slot `logo_placeholder` is a hand edit
 * too (landscape: above the royal jackpot, the base room's offset; portrait:
 * centred above the single jackpot row — a first guess), so attachLogo()
 * injects the logo exactly as in the other two rooms.
 *
 * The reel slots and the window clip are HAND EDITS in
 * hold_and_win_game_view.json (a re-export drops them): bone
 * `attachment_slot_placeholder` (-0.5, 9) under `reels` — the centre of the
 * 5x8 cell grid drawn into the frame — and clip `reels_window_clip`
 * (x -423.5..422.5, y 562..-544). The grid's cells are 170 x 139 spine units.
 */
export const HOLD_AND_WIN_GAME_VIEW_SLOTS = {
  REEL_SET: "holdAndWinGameView.reelSet",
  OVERLAY_REEL_SET: "holdAndWinGameView.overlayReelSet",
  // `respin_counter_placeholder` (hand-added, under the bottom row): the
  // standalone hold_and_win_counter spine — the respin counter now, the
  // End Collector later (the counter turns into it).
  RESPIN_COUNTER: "holdAndWinGameView.respinCounter",
};

// The 5x8 cell grid drawn into the frame, in spine units (nhw_reel_p).
export const HOLD_AND_WIN_CELL = { WIDTH: 170, HEIGHT: 139 };

// The coin counter on the Royal plate: filled cells / 40 (the Royal is all 40
// filled, GDD §8.9). Pot Collection's X/15 counter (GameViewController
// .updateCoinCounter), on this room's `txt_slot_royal_counter` slot. Spine
// units — the slot scales it with the plate.
export const HOLD_AND_WIN_COIN_COUNTER = {
  SLOT: "txt_slot_royal_counter",
  TOTAL: 40,
  FONT_SIZE: 30,
  MAX_WIDTH: 90, // the dark box on the plate; wider text is squeezed to fit
};
// The locked rows (animator, 2026-09-30: `locked_reel` — GDD "Locked-Row
// Symbols": "Show dormant symbols through a locked overlay until
// activation"). One plate per locked row, in the room's
// attachment_locked_reel_1..4 slots: _1 is data row 4 (the row directly above
// the active zone), _4 the top row. The slots sit last in the draw order, so
// a plate covers its row's cells and spines; its dark body is see-through,
// so the dormant symbols show under it. `idle` loops while the row is
// locked, `out` (0.5 s) plays as it opens. Replaces the black bars
// HoldAndWinOverlay drew with anime.js.
export const HOLD_AND_WIN_LOCKED_ROWS = {
  SLOT_PREFIX: "attachment_locked_reel_",
  FIRST_LOCKED_ROW: 4,
  COUNT: 4,
  SPINE: { SKELETON: "locked_reel-skeleton", ATLAS: "locked_reel-atlas" },
  IDLE: "idle",
  // `idle_N_still`: the same loop with the side padlocks at rest (client,
  // 2026-10-29: only the row that opens NEXT shakes its padlocks).
  STILL: "still",
  OUT: "out",
  // The plate's text: the active-zone count that opens the row (10 / 15 /
  // 20 / 25, ZONE_THRESHOLDS), left of the coin / scarab / coin icons.
  TEXT_SLOT: "txt_slot_locked_reel",
  // FONT_SIZE: 64,
  // MAX_WIDTH: 300,
  // "N MORE" (design, 2026-09-30): how many more active-zone symbols open
  // THIS row — its count minus cells_in_zone, updated as each one lands. The
  // Royal plate's X/40 counts the whole grid; this counts for its own row.
  // Spine units. The pair is right-aligned at RIGHT_EDGE (from the text
  // slot's centre), so "3 MORE" and "18 MORE" end at the same place.
  COUNT_FONT_SIZE: 68,
  MORE_FONT_SIZE: 48,
  GAP: 20,
  RIGHT_EDGE: 101,
  MORE_KEY: "hold_and_win_locked_row_more",
};

// The reference the padding is measured around: the BASE board (5x4 x 214),
// exactly as PixiSettings.REELSET_LAYOUT_CONFIGS measures it. So the same
// numbers give the same room as the base scale did, and each layout's
// numbers are a pure tuning knob from there (Vladimir, 2026-09-28).
const REFERENCE_WIDTH = Settings.REEL_COUNT * Settings.SYMBOL_WIDTH;
const REFERENCE_HEIGHT = Settings.SYMBOLS_VISIBLE * Settings.SYMBOL_HEIGHT;

// ============================================================================
// THIS ROOM'S OWN PADDING — per layout
//
// The base and free-spins rooms are drawn at the BASE board's scale (the
// 5x4 grid fitted by PixiSettings.REELSET_LAYOUT_CONFIGS). This room gets its
// own table so it can be tuned apart from them. Same formula, same reference
// board (see REFERENCE_WIDTH), capped at 1.0 like the base game (2.0 past
// ULTRA_WIDE) — so identical numbers draw it exactly as the base scale did.
//
// Same units as REELSET_LAYOUT_CONFIGS: multiples of Settings.SYMBOL_WIDTH
// (sides, both sides together) and SYMBOL_HEIGHT (top, bottom) — 214 — so the
// numbers read the same as the table in pixiSettings. Smaller padding =
// bigger room.
//
// Starting values: a straight copy of PixiSettings.REELSET_LAYOUT_CONFIGS
// (2026-09-28). Tune per layout from here.
// ============================================================================
export const HOLD_AND_WIN_LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: { sides: 0.35, top: 4.125, bottom: 4.125 },
  // MOBILE_SMALL_LANDSCAPE: { sides: 5.0, top: 0.7, bottom: 0.7 },
  // As MOBILE_LANDSCAPE (2026-10-16): its numbers fit every phone shape; the
  // small ones were measured on one 16:9 phone and left gaps on others.
  MOBILE_SMALL_LANDSCAPE: { sides: 5.25, top: 0.7, bottom: 0.7 },
  MOBILE_PORTRAIT: { sides: 0.35, top: 2.5, bottom: 2.5 },
  MOBILE_LANDSCAPE: { sides: 5.25, top: 0.7, bottom: 0.7 },
  // TABLET_PORTRAIT: { sides: 0.75, top: 3.5, bottom: 3.5 },
  // An iPad upright (2026-10-29): MOBILE_PORTRAIT's, more top / bottom — as
  // the base room's TABLET_PORTRAIT (pixiSettings).
  // TABLET_PORTRAIT: { sides: 0.35, top: 3.0, bottom: 3.0 },
  TABLET_PORTRAIT: { sides: 0.35, top: 3.7, bottom: 3.7 }, // ~12% smaller: fits between the top bar and BONUS
  // TABLET_LANDSCAPE: { sides: 5.25, top: 0.5, bottom: 0.5 },
  TABLET_LANDSCAPE: { sides: 5.25, top: 0.7, bottom: 0.7 }, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
  // SMALL_DESKTOP: { sides: 5.25, top: 0.5, bottom: 0.5 },
  // Short laptop windows (1536x730) ran the box off the top (2026-10-15, PO):
  // the padding shrinks the whole room there; width-bound windows unchanged.
  // SMALL_DESKTOP: { sides: 5.25, top: 0.67, bottom: 0.67 },
  // SMALL_DESKTOP: { sides: 5.25, top: 0.75, bottom: 0.75 }, // a tiny bit more air (2026-10-15)
  SMALL_DESKTOP: { sides: 5.25, top: 0.8, bottom: 0.8 }, // (2026-10-15)
  DESKTOP: { sides: 5.25, top: 0.85, bottom: 0.85 },
  WIDE_DESKTOP: { sides: 5.25, top: 0.85, bottom: 0.85 },
  ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 0.85, bottom: 0.85 },
};

// Moves the whole room, in multiples of SYMBOL_HEIGHT at the room's scale.
// y: POSITIVE = UP, x: POSITIVE = RIGHT — the same signs as
// GAME_VIEW_OFFSET_CONFIG.
export const HOLD_AND_WIN_OFFSET_CONFIG = {
  MOBILE_SMALL_PORTRAIT: { x: 0, y: 0 },
  MOBILE_SMALL_LANDSCAPE: { x: 0, y: 0.15 }, // pulled up, as mobile landscape (2026-10-09)
  MOBILE_PORTRAIT: { x: 0, y: 0.0 },
  MOBILE_LANDSCAPE: { x: 0, y: 0.15 }, // pulled up, as small desktop (2026-10-09)
  // TABLET_PORTRAIT: { x: 0, y: -0.55 },
  // TABLET_PORTRAIT: { x: 0, y: 0.0 }, // MOBILE_PORTRAIT's — an iPad upright (2026-10-29)
  // TABLET_PORTRAIT: { x: 0, y: 0.6 }, // lifted: the counter clear of the spin / BONUS row
  // TABLET_PORTRAIT: { x: 0, y: 0.46 }, // 0.6 / 1.3 — the room is zoomed 1.3 there
  // TABLET_PORTRAIT: { x: 0, y: 0.43 }, // 0.6 / 1.4 — the room is zoomed 1.4 there
  // TABLET_PORTRAIT: { x: 0, y: 0.4 }, // 0.6 / 1.5 — the room is zoomed 1.5 there
  // TABLET_PORTRAIT: { x: 0, y: 0.375 }, // 0.6 / 1.6 — the room is zoomed 1.6 there
  // TABLET_PORTRAIT: { x: 0, y: 0.353 }, // 0.6 / 1.7 — the room is zoomed 1.7 there
  TABLET_PORTRAIT: { x: 0, y: 0.364 }, // 0.6 / 1.65 — the room is zoomed 1.65 there
  // TABLET_LANDSCAPE: { x: 0, y: 0 },
  TABLET_LANDSCAPE: { x: 0, y: 0.15 }, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
  // SMALL_DESKTOP: { x: 0, y: 0.15 }, // pulled up (2026-10-09)
  SMALL_DESKTOP: { x: 0, y: 0.06 }, // the air shared top and bottom (2026-10-15)
  // DESKTOP: { x: 0, y: 0.1 },
  // A couple of pixels up (2026-10-23, 2436x1016: ~3 px) — a gap between the
  // toolbar and the respin counter.
  DESKTOP: { x: 0, y: 0.118 },
  // WIDE_DESKTOP: { x: 0, y: 0.1 },
  WIDE_DESKTOP: { x: 0, y: 0.118 }, // a bit up, as DESKTOP (2026-10-23, 2560x1016)
  // ULTRA_WIDE_DESKTOP: { x: 0, y: 0 },
  ULTRA_WIDE_DESKTOP: { x: 0, y: 0.15 }, // pulled up, off the toolbar (2026-10-22)
};

// ============================================================================
// THE REEL BOX — per layout, apart from the room (2026-09-29)
//
// The tables above size and place the WHOLE room (background included). This
// one scales only the reel box inside it — the frame, the 5x8 cells, the
// respin counter and the collector, which all hang off the `reels` bone —
// the way Mad Labs scaled its reels apart from its background.
//
// 1.0 = as the spine is authored (what is on screen today). 0.9 = the box 10%
// smaller, the background untouched. It scales about the box's own centre.
// Multiplies the landscape skin's own 0.8485 (the `reel_landscape`
// constraint), so 1.0 keeps landscape exactly as it is now.
// ============================================================================
export const HOLD_AND_WIN_REEL_SCALE_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.07, // a nudge smaller (2026-10-09)
  // MOBILE_SMALL_LANDSCAPE: 0.97, // a tiny bit bigger than mobile landscape (2026-10-09)
  // MOBILE_SMALL_LANDSCAPE: 0.959, // measured (2026-10-14)
  // MOBILE_SMALL_LANDSCAPE: 0.918, // as MOBILE_LANDSCAPE (2026-10-16)
  MOBILE_SMALL_LANDSCAPE: 0.957, // the counter just above the toolbar (2026-10-30, approved)
  // MOBILE_PORTRAIT: 1.0,
  // MOBILE_PORTRAIT: 1.03, // a bit bigger (2026-10-14)
  // MOBILE_PORTRAIT: 1.05, // bigger again (2026-10-14)
  // MOBILE_PORTRAIT: 1.06, // a couple of pixels more for the respins (2026-10-14)
  // Back a step (2026-10-15): with the 1.018 respin zoom the counter sat on the
  // play button on an iPhone 16 (393x852) — ~8 px above it now.
  MOBILE_PORTRAIT: 1.035,
  // MOBILE_LANDSCAPE: 0.95, // a tiny bit smaller than small desktop — topped out (2026-10-09)
  // MOBILE_LANDSCAPE: 0.92, // a little smaller (2026-10-13)
  MOBILE_LANDSCAPE: 0.918, // measured (2026-10-14)
  // TABLET_PORTRAIT: 1.0,
  // TABLET_PORTRAIT: 1.035, // MOBILE_PORTRAIT's — an iPad upright (2026-10-29)
  // TABLET_PORTRAIT: 0.98, // the reel a bit smaller (2026-10-29)
  TABLET_PORTRAIT: 1.01, // a bit bigger again, upward (2026-10-29)
  // TABLET_LANDSCAPE: 1.0,
  TABLET_LANDSCAPE: 0.918, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
  // SMALL_DESKTOP: 0.97, // a bit smaller, then a tiny bit back (2026-10-09)
  // SMALL_DESKTOP: 0.982, // measured (2026-10-14) — full-screen heights: into the toolbar in a browser
  // SMALL_DESKTOP: 0.912, // browser-window heights (2026-10-14)
  SMALL_DESKTOP: 0.956, // back to its size inside the 0.8-padded room (2026-10-15)
  // DESKTOP: 0.95, // a bit smaller (2026-10-09)
  // DESKTOP: 0.958, // measured (2026-10-14) — full-screen heights: into the toolbar in a browser
  DESKTOP: 0.944, // browser-window heights (2026-10-14)
  // WIDE_DESKTOP: 1.05, // a bit smaller (2026-10-09)
  // WIDE_DESKTOP: 1.118, // measured (2026-10-14) — full-screen heights: into the toolbar in a browser
  // WIDE_DESKTOP: 1.051, // browser-window heights (2026-10-14)
  // DESKTOP's (2026-10-23): a 2K window (~2560x1180) ran the box off the top
  // and onto the toolbar. Both layouts are height-bound there with the same
  // room padding, so desktop's box gives desktop's look.
  WIDE_DESKTOP: 0.944,
  // ULTRA_WIDE_DESKTOP: 0.98, // a nudge smaller (2026-10-09)
  // ULTRA_WIDE_DESKTOP: 0.975, // measured (2026-10-14) — full-screen heights: into the toolbar in a browser
  ULTRA_WIDE_DESKTOP: 0.959, // browser-window heights (2026-10-14)
};

// ── The landscape box's own height, apart from the room (2026-10-14) ──
// In multiples of SYMBOL_HEIGHT, y POSITIVE = UP. Measured with the
// collector-to-toolbar fit (each layout's tightest screen), then fixed here.
export const HOLD_AND_WIN_LANDSCAPE_BOX_OFFSET = {
  // MOBILE_SMALL_LANDSCAPE: 0.028,
  // MOBILE_SMALL_LANDSCAPE: 0.005, // as MOBILE_LANDSCAPE (2026-10-16)
  MOBILE_SMALL_LANDSCAPE: -0.105, // down by half the bigger box (2026-10-30, approved)
  MOBILE_LANDSCAPE: 0.005,
  TABLET_LANDSCAPE: 0.005, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
  // SMALL_DESKTOP: -0.028,
  // SMALL_DESKTOP: 0.143,
  SMALL_DESKTOP: 0.162, // the 0.956 box, top 8 px under 1536x730's top (2026-10-15)
  // DESKTOP: -0.021,
  DESKTOP: 0.012,
  // WIDE_DESKTOP: -0.167,
  // WIDE_DESKTOP: -0.004,
  WIDE_DESKTOP: 0.012, // DESKTOP's, with its box (2026-10-23)
  // ULTRA_WIDE_DESKTOP: 0.013,
  ULTRA_WIDE_DESKTOP: 0.051,
};

// ============================================================================
// PORTRAIT: reel box + jackpots + logo AS ONE (2026-09-29)
//
// In landscape the three sit apart (the box on the right, the jackpot column
// and the logo on the left) and HOLD_AND_WIN_REEL_SCALE_CONFIG sizes the box
// alone. In portrait they are one stack — logo, jackpot row, box — so they
// scale and move TOGETHER, the background staying where it is.
//
//   scale  1.0 = as authored. About the scene's centre, so the stack keeps
//          its own proportions and spacing.
//   x, y   in multiples of SYMBOL_HEIGHT at the room's scale, the same unit
//          and signs as HOLD_AND_WIN_OFFSET_CONFIG: y POSITIVE = UP,
//          x POSITIVE = RIGHT.
//
// Portrait layouts only; a layout with no entry is left as authored.
// HOLD_AND_WIN_REEL_SCALE_CONFIG still multiplies the box on top of this.
//
//   reelY  the box AND its jackpot row (reels + _anchor) only, on top of y —
//          the logo stays where the group puts it. Same unit and sign.
//   rowOnFrame  the frame's TOP stays where it is when the box is scaled
//          (HOLD_AND_WIN_REEL_SCALE_CONFIG): the box grows about its centre,
//          so it drops by half its frame x (scale - 1) — the gap to the logo
//          and the jackpot row on the top bar stay as they are.
// ============================================================================
export const HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG = {
  // MOBILE_SMALL_PORTRAIT: { scale: 1.02, x: 0, y: -0.45 },
  // MOBILE_SMALL_PORTRAIT: { scale: 1.016, x: 0, y: -0.43 }, // measured (2026-10-14)
  // The room zoomed 1.2 (HOLD_AND_WIN_ROOM_ZOOM_CONFIG): the stack / 1.2, so it stays put.
  MOBILE_SMALL_PORTRAIT: { scale: 0.847, x: 0, y: -0.358 },
  // MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.85 },
  // MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.75 }, // pulled back up a little (2026-10-14)
  // + the box and its jackpot row closer to the logo, the logo staying put
  // (2026-10-14: "pull the reel a bit closer to logo … without logo").
  // MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.75, reelY: 0.2 },
  // MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.75, reelY: 0.15 }, // a bit less (2026-10-14)
  // MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.75, reelY: 0.15, rowOnFrame: true },
  // MOBILE_PORTRAIT: { scale: 0.992, x: 0, y: -0.71, reelY: 0.149, rowOnFrame: true }, // measured (2026-10-14)
  // The room zoomed 1.1 (HOLD_AND_WIN_ROOM_ZOOM_CONFIG): the stack / 1.1, so it stays put.
  // MOBILE_PORTRAIT: { scale: 0.902, x: 0, y: -0.645, reelY: 0.135, rowOnFrame: true },
  MOBILE_PORTRAIT: { scale: 0.863, x: 0, y: -0.617, reelY: 0.13, rowOnFrame: true }, // / 1.15
  // TABLET_PORTRAIT: { scale: 1.0, x: 0, y: 0 },
  // The collector sat ON the play button on iPads (-7..0 px): the phones' gap
  // now, about the logo (2026-10-14, measured 768x1024 .. 1024x1366).
  // TABLET_PORTRAIT: { scale: 0.976, x: 0, y: 0.12 },
  // MOBILE_PORTRAIT's — an iPad upright (2026-10-29).
  // TABLET_PORTRAIT: { scale: 0.992, x: 0, y: -0.71, reelY: 0.149, rowOnFrame: true },
  // The room zoomed 1.3 there (HOLD_AND_WIN_ROOM_ZOOM_CONFIG): the stack and
  // its offsets / 1.3, so it stays where it was (2026-10-29).
  // TABLET_PORTRAIT: { scale: 0.763, x: 0, y: -0.546, reelY: 0.115, rowOnFrame: true },
  // TABLET_PORTRAIT: { scale: 0.709, x: 0, y: -0.507, reelY: 0.106, rowOnFrame: true }, // / 1.4
  // TABLET_PORTRAIT: { scale: 0.661, x: 0, y: -0.473, reelY: 0.099, rowOnFrame: true }, // / 1.5
  // The counter right above the play button, as on the phones (measured).
  // TABLET_PORTRAIT: { scale: 0.661, x: 0, y: -0.473, reelY: -0.09, rowOnFrame: true },
  // The smaller reel back on the play button, the logo down (its fire on screen).
  // TABLET_PORTRAIT: { scale: 0.661, x: 0, y: -0.68, reelY: -0.14, rowOnFrame: true },
  // TABLET_PORTRAIT: { scale: 0.661, x: 0, y: -0.75, reelY: -0.07, rowOnFrame: true }, // the logo alone a tiny bit down
  // TABLET_PORTRAIT: { scale: 0.62, x: 0, y: -0.703, reelY: -0.066, rowOnFrame: true }, // x 1.5 / 1.6 (room zoom 1.6)
  // TABLET_PORTRAIT: { scale: 0.584, x: 0, y: -0.662, reelY: -0.062, rowOnFrame: true }, // x 1.6 / 1.7 (room zoom 1.7)
  // TABLET_PORTRAIT: { scale: 0.602, x: 0, y: -0.682, reelY: -0.064, rowOnFrame: true }, // x 1.7 / 1.65 (room zoom 1.65)
  // TABLET_PORTRAIT: { scale: 0.602, x: 0, y: -0.762, reelY: 0.016, rowOnFrame: true }, // the logo alone a bit closer to the reel
  // TABLET_PORTRAIT: { scale: 0.602, x: 0, y: -0.802, reelY: 0.056, rowOnFrame: true }, // a tiny bit more
  // TABLET_PORTRAIT: { scale: 0.602, x: 0, y: -0.802, reelY: 0.18, rowOnFrame: true }, // the bigger reel up by its growth: same gap to the play button
  TABLET_PORTRAIT: { scale: 0.602, x: 0, y: -0.678, reelY: 0.056, rowOnFrame: true }, // the bigger reel up by its growth, the logo with it
};

// The group's bones — all children of the root, so one transform about the
// root moves them as a rigid whole:
//   reels                     the box (frame, cells, counter, collector)
//   _anchor                   the portrait jackpot plates' constraint targets
//                             (land_jckp_*2, major_portrait), so the plates
//                             follow it
//   attachment_logo_portrait  the logo's portrait slot
const PORTRAIT_GROUP_BONES = ["reels", "_anchor", "attachment_logo_portrait"];
// ── The portrait jackpot row as long as the frame's top edge (2026-10-14:
// "they should be as long as top edge of reel not smaller") ── the row's
// targets (under _anchor, the transform constraints' sources) are spread and
// scaled about the row's centre so it spans the top bar, which grows with the
// box (the layout factor x the zoom bone's respin size); each plate grows UP
// from its bottom edge, staying on the bar. Art units: the plate's half width
// and its bone's height above the plate's bottom edge.
const FRAME_TOP_BAR_WIDTH = 1020; // reelfreme_hnw's top bar (x 92..1111 of 1197)
const PORTRAIT_JACKPOT_ROW = [
  { bone: "land_jckp_mini2", halfWidth: 115.5, aboveBottom: 57 },
  { bone: "land_jckp_minor2", halfWidth: 115.5, aboveBottom: 57 },
  { bone: "land_jckp_grand2", halfWidth: 143.5, aboveBottom: 74 },
  { bone: "land_jckp_royal2", halfWidth: 150, aboveBottom: 73.72 },
  { bone: "major_portrait", halfWidth: 134.5, aboveBottom: 61 },
  { bone: "land_jckp_midi2", halfWidth: 134.5, aboveBottom: 60.5 },
];
// Half the H&W frame's height (`reelfreme_hnw`, 1236, centred on `reels`).
const HOLD_AND_WIN_FRAME_HALF_HEIGHT = 618;

// ── The box never reaches the play button (2026-10-14) ── the UI does not
// move, and the gap between the counter / collector under the box and the
// play button changes with every screen's shape. In portrait the room
// measures the button and fits the box (fitBoxToPlayButton): the layout's
// HOLD_AND_WIN_REEL_SCALE_CONFIG is the MOST it may be, never exceeded.
//   MARGIN_PX  clear space kept above the button, screen px
//   MIN_SCALE  the smallest box the fit may choose
// const PLAY_BUTTON_FIT = { MARGIN_PX: 4, MIN_SCALE: 0.7 };
// const PLAY_BUTTON_FIT = { MARGIN_PX: 4, MIN_SCALE: 0.7, MAX_SCALE: 1.6 };
// No clamps (2026-10-14: "i never told you to clamp anything"): the fit is
// whatever puts the collector on its edge.
const PLAY_BUTTON_FIT = { MARGIN_PX: 4 };
// Landscape: the respin counter's gap to the toolbar, the same on every screen
// (2026-10-22: "respin counter must never go under toolbar … always the same
// gap"), measured from the counter's REAL bottom (see fitLandscapeBox).
const LANDSCAPE_TOOLBAR_GAP_PX = 8;
// The layouts fitted — portrait PHONES, standard and small (2026-10-14).
const PLAY_BUTTON_FIT_LAYOUTS = ["MOBILE_PORTRAIT", "MOBILE_SMALL_PORTRAIT"];
// ── Landscape, automatic too (2026-10-14: "make this smart and automatic") ──
// The box alone (`reel_landscape`) grows / shrinks about its TOP until the
// first thing it would touch: the bottom toolbar's top edge (under the
// collector), the jackpot column and logo (left), the toolbar's buttons that
// stand up beside it or the screen edge (right).
const LANDSCAPE_FIT_LAYOUTS = [
  "MOBILE_LANDSCAPE",
  "MOBILE_SMALL_LANDSCAPE",
  "TABLET_LANDSCAPE",
  "SMALL_DESKTOP",
  "DESKTOP",
  "WIDE_DESKTOP",
  "ULTRA_WIDE_DESKTOP",
];
// The bottom toolbar (secondary ToolbarBottom's container).
const BOTTOM_TOOLBAR_NAME = "secondary_toolbar_bottom";
// Half the frame's solid width (`reelfreme_hnw`, centred on `reels`).
const HOLD_AND_WIN_FRAME_HALF_WIDTH = 597.5;
// The landscape jackpot plates and their half widths (their art).
const LANDSCAPE_PLATES = {
  land_jckp_mini: 115.5,
  land_jckp_minor: 115.5,
  land_jckp_grand: 143.5,
  land_jckp_royal: 150,
  land_jckp_major: 134.5,
  land_jckp_midi: 134.5,
};
// The slot the respin counter and the collector hang from.
const COUNTER_SLOTS = ["attachment_spin_counter", "respin_counter_placeholder"];

/**
 * The scale this room's spine is drawn at, from its own padding table. Used
 * by the controller for the spine and by HoldAndWinGameView for the counter-
 * scale of the injected board, so the two always agree.
 */
export const holdAndWinSceneScale = (dimensions, layout) => {
  if (!dimensions?.width || !dimensions?.height) return 1;
  const pad = HOLD_AND_WIN_LAYOUT_CONFIGS[layout] ?? {
    sides: 0,
    top: 0,
    bottom: 0,
  };
  const neededWidth = REFERENCE_WIDTH + pad.sides * Settings.SYMBOL_WIDTH;
  const neededHeight =
    REFERENCE_HEIGHT + (pad.top + pad.bottom) * Settings.SYMBOL_HEIGHT;
  // const fit = Math.min(
  //   dimensions.width / neededWidth,
  //   dimensions.height / neededHeight,
  // );
  // The height is the UI's 16:9 box (FEATURES_CONFIG.LAYOUT.NO_TABLET): a
  // frame narrower than 16:9 fits the room inside the HUD block, as the base.
  const fit = Math.min(
    dimensions.width / neededWidth,
    (dimensions.uiBoxHeight ?? dimensions.height) / neededHeight,
  );
  const cap =
    dimensions.width > ULTRA_WIDE.BREAKPOINT ? ULTRA_WIDE.MAX_SCALE : 1;
  // return Math.min(cap, fit);
  // Never wider than the base game view's art on screen (2026-10-22: "match
  // them so they have the same width") — `hnw_bg` is HNW_BG_UNITS wide.
  // Shrinks only; where this room is already the narrower, nothing changes.
  const artWidth = getGameViewArtWidth();
  const matchBase = artWidth > 0 ? artWidth / HNW_BG_UNITS : Infinity;
  // return Math.min(cap, fit, matchBase);
  // ...then the room's own zoom (HOLD_AND_WIN_ROOM_ZOOM_CONFIG).
  return Math.min(cap, fit, matchBase) * (HOLD_AND_WIN_ROOM_ZOOM_CONFIG[layout] ?? 1);
};

// ── The whole room zoomed, per layout (2026-10-29) ── on an iPad upright the
// palms of the background stood over the reel's lower corners: the room is
// zoomed so they go out to the corners, and the portrait stack (logo, jackpot
// row, reel) is scaled back by its group row (HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG)
// so it stays as it was. 1 = none.
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.3 };
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.4 }; // a bit more
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.5 };
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.6 }; // a bit more
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.7 };
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.65 };
// + phones upright: a notch, the palms off the symbols (2026-10-29).
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.65, MOBILE_PORTRAIT: 1.1 };
// const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = { TABLET_PORTRAIT: 1.65, MOBILE_PORTRAIT: 1.15 };
const HOLD_AND_WIN_ROOM_ZOOM_CONFIG = {
  TABLET_PORTRAIT: 1.65,
  MOBILE_PORTRAIT: 1.15,
  MOBILE_SMALL_PORTRAIT: 1.2,
};

// `hnw_bg`'s width in spine units (-1300..+1300).
const HNW_BG_UNITS = 2600;

// The room's background art zoomed a touch per layout, so it reaches the
// screen's edges where the room is narrower than the window (2026-10-22).
// const HNW_BG_ZOOM_CONFIG = { ULTRA_WIDE_DESKTOP: 1.06 };
// + WIDE_DESKTOP (2026-10-23): a 2K window left ~24 px empty at each side.
const HNW_BG_ZOOM_CONFIG = { WIDE_DESKTOP: 1.05, ULTRA_WIDE_DESKTOP: 1.06 };

/**
 * Where this room goes on screen and at what scale — the one placement used
 * by the spine (applyResponsiveScaling) and by HoldAndWinGameView's backup
 * when the board has no spine slot. `reelScale` is the reel box's layout
 * factor (HOLD_AND_WIN_REEL_SCALE_CONFIG).
 */
export const resolveHoldAndWinPlacement = (dimensions, layout) => {
  const scale = holdAndWinSceneScale(dimensions, layout);
  const offset = HOLD_AND_WIN_OFFSET_CONFIG[layout] ?? { x: 0, y: 0 };
  const unit = Settings.SYMBOL_HEIGHT * scale;
  return {
    // Y is SUBTRACTED so a positive value pushes the room up.
    x: (dimensions?.width ?? 0) / 2 + unit * (offset.x ?? 0),
    y: (dimensions?.height ?? 0) / 2 - unit * (offset.y ?? 0),
    scale,
    reelScale: HOLD_AND_WIN_REEL_SCALE_CONFIG[layout] ?? 1,
  };
};

// The landscape jackpot column in this room (applyColumnMatch): moved from
// where its right edge was in this room's own setup, in this room's spine
// units — x +right, y +up. Mobile landscape: up over the brazier at the
// pillar's foot and close to the pillar (2026-10-16).
export const HOLD_AND_WIN_COLUMN_NUDGE = {
  DEFAULT: { x: 75, y: 0 },
  // MOBILE_LANDSCAPE: { x: 196, y: 214 },
  // MOBILE_SMALL_LANDSCAPE: { x: 196, y: 214 },
  // MOBILE_LANDSCAPE: { x: 196, y: 110 }, // 214 lifted it far too much
  // MOBILE_SMALL_LANDSCAPE: { x: 196, y: 110 },
  MOBILE_LANDSCAPE: { x: 160, y: 80 }, // a bit lower, a bit further from the pillar
  MOBILE_SMALL_LANDSCAPE: { x: 160, y: 80 },
  TABLET_LANDSCAPE: { x: 160, y: 80 }, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
};

class HoldAndWinGameViewController extends GameViewSimpleController {
  static SCENE = {
    TAG: "HoldAndWinGameView",
    SKELETON: "hold_and_win_game_view-skeleton",
    ATLAS: "hold_and_win_game_view-atlas",
    SPINE_NAME: "hold_and_win_game_view",
    REEL_SET_KEY: HOLD_AND_WIN_GAME_VIEW_SLOTS.REEL_SET,
    OVERLAY_REEL_SET_KEY: HOLD_AND_WIN_GAME_VIEW_SLOTS.OVERLAY_REEL_SET,
    IDLE_ANIMATIONS: ["hnw_bg_idle", "idle"],
  };

  /**
   * Fit this room with ITS OWN padding (HOLD_AND_WIN_LAYOUT_CONFIGS) instead
   * of the base board's scale, and place it with its own offset table.
   */
  applyResponsiveScaling() {
    if (!this.dimensions || !this.spine) return;
    const placement = resolveHoldAndWinPlacement(this.dimensions, this.layout);
    this.spine.scale.set(placement.scale);
    this.spine.position.set(placement.x, placement.y);
    this.applyColumnMatch();
    // // The background covers the window whatever the padding (2026-10-15).
    // this.coverBackground();
    // (off — this room's background was right as it was; the base room's only)
    // On again (2026-10-22: "hold and win just background to cover edges"):
    // its background art only, out to the screen edges — see coverBackground.
    // this.coverBackground();
    // Not a per-window stretch (2026-10-22: "i don't want full stretch at all
    // times … as base game, a tiny bit more zoomed"): a FIXED zoom of its
    // background art per layout (HNW_BG_ZOOM_CONFIG), never bg_master's palms.
    const zoom = HNW_BG_ZOOM_CONFIG[this.layout] ?? 1;
    for (const name of ["bg_1", "bg_2"]) {
      const bone = this.spine.skeleton?.findBone(name);
      if (!bone) continue;
      bone.scaleX = bone.data.scaleX * zoom;
      bone.scaleY = bone.data.scaleY * zoom;
    }
  }

  /**
   * ── The jackpot column where the base room has it (2026-10-16) ── in
   * landscape, this room's jackpot plates and logo are drawn at the SAME place
   * and size on screen as in the base / free-spins rooms (their spine setup,
   * their placement and scale, LANDSCAPE_COLUMN_OFFSET_CONFIG), whatever this
   * room's own scale. Set each frame after the animation, taken off after the
   * world transforms (as GameViewSimpleController.applyColumnOffset), so this
   * room's spine is never changed. The reels are untouched. Portrait: its own.
   */
  applyColumnMatch() {
    const spine = this.spine;
    if (!spine?.skeleton) return;
    if (this._columnMatchHooked) return;
    this._columnMatchHooked = true;
    const bones = LANDSCAPE_COLUMN_BONES.map((n) => spine.skeleton.findBone(n)).filter(Boolean);
    const restore = new Map();
    // spine.beforeUpdateWorldTransforms = () => {
    this._onWorldHook("before", "columnMatch", () => {
      restore.clear();
      const base = getBaseColumnSetup();
      const layout = this.layout;
      if (!base || !this.dimensions || !/LANDSCAPE|DESKTOP/.test(String(layout))) return;
      const board = resolveBoardPlacement({
        dimensions: this.dimensions,
        symbolHeight: this.symbolHeight,
        symbolScale: this.symbolScale,
        layout,
      });
      const k = spine.scale.x;
      if (!board.scale || !k) return;
      const lift = (LANDSCAPE_COLUMN_OFFSET_CONFIG[layout]?.y ?? 0) * Settings.SYMBOL_HEIGHT;
      // ── As close to the reels as this room had it (2026-10-16) ── the
      // column ends (its right edge) exactly where this room's own column
      // ended next to its reels; only its size and height are the base
      // room's. COLUMN_HALF_WIDTH: the plates' half width in spine units.
      const COLUMN_HALF_WIDTH = 150;
      // // Closer still (2026-10-16): this room's spine units, +right.
      // const COLUMN_NUDGE_X = 75;
      // Per layout, this room's spine units: x +right, y +up (2026-10-16).
      const nudge = HOLD_AND_WIN_COLUMN_NUDGE[layout] ?? HOLD_AND_WIN_COLUMN_NUDGE.DEFAULT;
      for (const bone of bones) {
        const b = base[bone.data.name];
        if (!b) continue;
        restore.set(bone, [bone.x, bone.y, bone.scaleX, bone.scaleY]);
        // Screen = position + spine units × scale (y the spine's way up).
        // bone.x = (board.x + b.x * board.scale - spine.position.x) / k;
        // Right edge where this room's own setup had it (its setup scale).
        bone.x =
          bone.data.x +
          nudge.x +
          COLUMN_HALF_WIDTH * bone.data.scaleX -
          (COLUMN_HALF_WIDTH * b.scaleX * board.scale) / k;
        // bone.y = (spine.position.y - board.y + (b.y + lift) * board.scale) / k;
        bone.y = (spine.position.y - board.y + (b.y + lift) * board.scale) / k + nudge.y;
        bone.scaleX = (b.scaleX * board.scale) / k;
        bone.scaleY = (b.scaleY * board.scale) / k;
      }
    });
    // spine.afterUpdateWorldTransforms = () => {
    this._onWorldHook("after", "columnMatch", () => {
      for (const [bone, [x, y, sx, sy]] of restore) {
        bone.x = x;
        bone.y = y;
        bone.scaleX = sx;
        bone.scaleY = sy;
      }
    });
  }

  /**
   * The reel box's own scale (HOLD_AND_WIN_REEL_SCALE_CONFIG), in place of the
   * base game's table. On BOTH bones, each times its own setup scale:
   *   `reels`           the box itself — used as is in portrait;
   *   `reel_landscape`  the landscape skin's constraint COPIES this bone's
   *                     transform onto `reels`, so a scale on `reels` alone
   *                     would be overwritten there.
   * Re-applied on every resize, after the layout skin (see resize).
   */
  applyReelBlockScale() {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return;
    const factor = HOLD_AND_WIN_REEL_SCALE_CONFIG[this.layout] ?? 1;

    // Landscape's box: the constraint target.
    const landscapeBox = skeleton.findBone("reel_landscape");
    if (landscapeBox) {
      // landscapeBox.scaleX = landscapeBox.data.scaleX * factor;
      // landscapeBox.scaleY = landscapeBox.data.scaleY * factor;
      // Fitted to its surroundings (fitLandscapeBox), about its frame's top.
      const fitL = this._fitLandscapeScale ?? 1;
      landscapeBox.scaleX = landscapeBox.data.scaleX * factor * fitL;
      landscapeBox.scaleY = landscapeBox.data.scaleY * factor * fitL;
      landscapeBox.y =
        landscapeBox.data.y +
        (HOLD_AND_WIN_LANDSCAPE_BOX_OFFSET[this.layout] ?? 0) * Settings.SYMBOL_HEIGHT +
        HOLD_AND_WIN_FRAME_HALF_HEIGHT * landscapeBox.data.scaleY * factor * (1 - fitL) -
        // ...and down onto the toolbar when a side stopped its growth.
        (this._fitLandscapeDrop ?? 0);
    }

    // The portrait group (identity in landscape — no entry): each bone's
    // setup position scaled about the root plus the offset, its setup scale
    // times the group's. The box also takes its own factor.
    const group = HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG[this.layout];
    const groupScale = group?.scale ?? 1;
    const offsetX = (group?.x ?? 0) * Settings.SYMBOL_HEIGHT;
    const offsetY = (group?.y ?? 0) * Settings.SYMBOL_HEIGHT;
    // The box and its jackpot row only — not the logo.
    const reelOffsetY = (group?.reelY ?? 0) * Settings.SYMBOL_HEIGHT;
    // The row on the scaled frame's top bar (rowOnFrame).
    const rowRise = group?.rowOnFrame
      ? HOLD_AND_WIN_FRAME_HALF_HEIGHT * (factor - 1) * groupScale
      : 0;
    // // The box as the play button allows (fitBoxToPlayButton) — never bigger
    // // than the layout's own. Smaller, it shrinks about its frame's TOP: the
    // // gap to the logo and the jackpot row stay, only its bottom comes up.
    // const boxScale =
    //   group && this._fitReelScale != null ? Math.min(factor, this._fitReelScale) : factor;
    // const fitShift = group
    //   ? HOLD_AND_WIN_FRAME_HALF_HEIGHT * (factor - boxScale) * groupScale
    //   : 0;
    // ── The whole stack fitted to the play button (2026-10-14: "there is room
    // we are just not using it … logo can grow too … sides can overflow") ──
    // logo, jackpot row and box scaled TOGETHER by `fit` about the logo's
    // place (fitBoxToPlayButton): bigger where the screen has the room,
    // smaller where it has not. 1 until the room has measured.
    const fit = group && this._fitGroupScale != null ? this._fitGroupScale : 1;
    const logoBone = skeleton.findBone("attachment_logo_portrait");
    const pivotX = offsetX;
    const pivotY = logoBone ? logoBone.data.y * groupScale + offsetY : 0;
    for (const name of PORTRAIT_GROUP_BONES) {
      const bone = skeleton.findBone(name);
      if (!bone) continue;
      // const k = groupScale * (name === "reels" ? factor : 1);
      // const k = groupScale * (name === "reels" ? boxScale : 1);
      const k = groupScale * (name === "reels" ? factor : 1) * fit;
      // bone.x = bone.data.x * groupScale + offsetX;
      bone.x = pivotX + (bone.data.x * groupScale + offsetX - pivotX) * fit;
      // bone.y = bone.data.y * groupScale + offsetY;
      const y0 =
        bone.data.y * groupScale +
        offsetY +
        (name === "attachment_logo_portrait" ? 0 : reelOffsetY) +
        // (name === "_anchor" ? rowRise : 0);
        // The frame's TOP stays put (2026-10-14: "reel went too far up,
        // bring it back where it was from logo"): the box drops by the rise,
        // so it grows downward only and the row stays where it was.
        (name === "reels" ? -rowRise : 0);
      // (name === "reels" ? fitShift - rowRise : 0);
      bone.y = pivotY + (y0 - pivotY) * fit;
      bone.scaleX = bone.data.scaleX * k;
      bone.scaleY = bone.data.scaleY * k;
    }
    this._fitPortraitJackpotRow(group ? factor : null);
  }

  /**
   * The portrait jackpot row spans the frame's top bar (PORTRAIT_JACKPOT_ROW).
   * `factor` is the box's own scale over the row's (null in landscape: the
   * row's bones back to setup).
   */
  _fitPortraitJackpotRow(factor) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return;
    const plates = PORTRAIT_JACKPOT_ROW.map((p) => ({ ...p, b: skeleton.findBone(p.bone) })).filter(
      (p) => p.b,
    );
    if (!plates.length) return;
    let left = Infinity;
    let right = -Infinity;
    for (const { b, halfWidth } of plates) {
      left = Math.min(left, b.data.x - halfWidth * b.data.scaleX);
      right = Math.max(right, b.data.x + halfWidth * b.data.scaleX);
    }
    const zoom = skeleton.findBone(REEL_ZOOM.BONE)?.data.scaleX ?? 1;
    const rowScale =
      factor == null ? 1 : (FRAME_TOP_BAR_WIDTH * factor * zoom) / (right - left);
    const centre = (left + right) / 2;
    for (const { b, aboveBottom } of plates) {
      b.x = centre + (b.data.x - centre) * rowScale;
      b.y = b.data.y + aboveBottom * b.data.scaleY * (rowScale - 1);
      b.scaleX = b.data.scaleX * rowScale;
      b.scaleY = b.data.scaleY * rowScale;
    }
  }

  // ── Box-only, shrink-only first version (2026-10-14), replaced below ──
  // fitBoxToPlayButton(button) {
  //   const skeleton = this.spine?.skeleton;
  //   const group = HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG[this.layout];
  //   const slotName = COUNTER_SLOTS.find((n) => skeleton?.findSlot(n));
  //   const usable =
  //     group && slotName && button && !button.destroyed && button.worldTransform;
  //   if (!usable) {
  //     if (this._fitReelScale != null) {
  //       this._fitReelScale = null;
  //       this.applyReelBlockScale();
  //     }
  //     return;
  //   }
  //   const wt = button.worldTransform;
  //   const buttonTop =
  //     wt.ty - ((button.texture?.orig?.height ?? 0) * Math.hypot(wt.c, wt.d)) / 2;
  //   const target = buttonTop - PLAY_BUTTON_FIT.MARGIN_PX;
  //   const bone = skeleton.findSlot(slotName).bone;
  //   // Spine 4.2 always wants the physics mode (undefined throws).
  //   const physics = Physics.update;
  //   // The counter's bottom on screen with the box at `scale`.
  //   const bottomAt = (scale) => {
  //     this._fitReelScale = scale;
  //     this.applyReelBlockScale();
  //     skeleton.updateWorldTransform(physics);
  //     const p = bone.localToWorld(new Vector2(0, -COLLECTOR_BOTTOM));
  //     this.spine.skeletonToPixiWorldCoordinates(p); // in place
  //     return p.y;
  //   };
  //   const factor = HOLD_AND_WIN_REEL_SCALE_CONFIG[this.layout] ?? 1;
  //   const atFull = bottomAt(factor);
  //   if (atFull <= target) {
  //     this._fitReelScale = null;
  //     this.applyReelBlockScale();
  //     return;
  //   }
  //   const step = 0.1;
  //   const atLess = bottomAt(factor - step);
  //   const slope = (atFull - atLess) / step;
  //   const fitted = slope > 0 ? factor - (atFull - target) / slope : factor;
  //   this._fitReelScale = Math.max(PLAY_BUTTON_FIT.MIN_SCALE, Math.min(factor, fitted));
  //   this.applyReelBlockScale();
  // }

  /**
   * Fit the portrait stack — logo, jackpot row and box together — so the
   * collector under the box lands just above the play button (PLAY_BUTTON_FIT):
   * it GROWS where the screen has room and shrinks where it has not; the
   * sides may overflow. Scaled about the logo's place. Portrait only — in
   * landscape the button is beside the box. Called by the room a frame after
   * a resize, once the button stands where the new size puts it. `button` is
   * the play button's sprite (anchor 0.5).
   */
  fitBoxToPlayButton(button) {
    // Landscape has its own fit (the box beside the column and the buttons).
    this.fitLandscapeBox();
    const skeleton = this.spine?.skeleton;
    const group = HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG[this.layout];
    const slotName = COUNTER_SLOTS.find((n) => skeleton?.findSlot(n));
    const usable =
      group &&
      PLAY_BUTTON_FIT_LAYOUTS.includes(this.layout) &&
      slotName &&
      button &&
      !button.destroyed &&
      button.worldTransform;
    if (!usable) {
      if (this._fitGroupScale != null) {
        this._fitGroupScale = null;
        this.applyReelBlockScale();
      }
      return;
    }
    const wt = button.worldTransform;
    const buttonTop =
      wt.ty - ((button.texture?.orig?.height ?? 0) * Math.hypot(wt.c, wt.d)) / 2;
    const target = buttonTop - PLAY_BUTTON_FIT.MARGIN_PX;
    const bone = skeleton.findSlot(slotName).bone;
    // Spine 4.2 always wants the physics mode (undefined throws).
    const physics = Physics.update;
    // The collector's bottom on screen with the stack at `fit`.
    const bottomAt = (fit) => {
      this._fitGroupScale = fit;
      this.applyReelBlockScale();
      skeleton.updateWorldTransform(physics);
      const p = bone.localToWorld(new Vector2(0, -COLLECTOR_BOTTOM));
      this.spine.skeletonToPixiWorldCoordinates(p); // in place
      return p.y;
    };
    // The bottom moves in a straight line with the fit (a scale about a
    // fixed point): two measures give the fit that lands it on the target.
    const step = 0.1;
    const atOne = bottomAt(1);
    const atMore = bottomAt(1 + step);
    const slope = (atMore - atOne) / step;
    const fitted = slope > 0 ? 1 + (target - atOne) / slope : 1;
    this._fitGroupScale = Number.isFinite(fitted) && fitted > 0 ? fitted : 1;
    this.applyReelBlockScale();
  }

  /**
   * Landscape: fit the box (`reel_landscape`) to what is around it — see
   * LANDSCAPE_FIT_LAYOUTS. Every edge moves in a straight line with the fit
   * (a scale about the frame's top), so two measures solve each limit; the
   * tightest one wins. Measured live: the toolbar and its buttons as the UI
   * placed them, the plates and the logo as the spine poses them.
   */
  fitLandscapeBox() {
    const skeleton = this.spine?.skeleton;
    const reels = skeleton?.findBone("reels");
    const box = skeleton?.findBone("reel_landscape");
    const slotName = COUNTER_SLOTS.find((n) => skeleton?.findSlot(n));
    const reset = () => {
      if (this._fitLandscapeScale != null || this._fitLandscapeDrop) {
        this._fitLandscapeScale = null;
        this._fitLandscapeDrop = 0;
        this.applyReelBlockScale();
      }
    };
    if (!LANDSCAPE_FIT_LAYOUTS.includes(this.layout) || !reels || !box || !slotName) {
      reset();
      return;
    }
    // The bottom toolbar, from the stage.
    let root = this.container;
    while (root?.parent) root = root.parent;
    const toolbar = root?.getChildByName?.(BOTTOM_TOOLBAR_NAME, true);
    if (!toolbar?.worldTransform) {
      reset();
      return;
    }
    const screenW = this.dimensions?.width ?? 0;
    const toolbarTop = toolbar.worldTransform.ty;
    // Its buttons that stand up into the play area on the right.
    let rightLimit = screenW;
    for (const child of toolbar.children) {
      if (!child.visible || child.worldAlpha <= 0.01) continue;
      const b = child.getBounds();
      if (!(b.width > 0) || b.width >= screenW * 0.9) continue; // the bar itself
      if (b.top >= toolbarTop - 1) continue; // inside the bar
      if (b.x + b.width / 2 > screenW / 2) rightLimit = Math.min(rightLimit, b.left);
    }
    const physics = Physics.update;
    const pixi = (bone, x, y) => {
      const p = bone.localToWorld(new Vector2(x, y));
      this.spine.skeletonToPixiWorldCoordinates(p); // in place
      return p;
    };
    // The jackpot column and the logo: the left limit.
    skeleton.updateWorldTransform(physics);
    let leftLimit = 0;
    for (const [name, half] of Object.entries(LANDSCAPE_PLATES)) {
      const plate = skeleton.findBone(name);
      if (plate) leftLimit = Math.max(leftLimit, pixi(plate, half, 0).x);
    }
    if (this.logoSpine && !this.logoSpine.destroyed) {
      const lb = this.logoSpine.getBounds();
      if (lb.width > 0) leftLimit = Math.max(leftLimit, lb.right);
    }
    const counter = skeleton.findSlot(slotName).bone;
    // How far the counter's art hangs below the point the fit measures
    // (COLLECTOR_BOTTOM) — read off the counter as last drawn. Without it the
    // fit put that POINT on the toolbar and the counter's own bottom under it
    // on a short screen.
    let hang = 0;
    const drawn = this.respinCounterSlotContainer;
    if (drawn && !drawn.destroyed && drawn.worldVisible) {
      const cb = drawn.getBounds();
      if (cb.height > 0) hang = Math.max(0, cb.bottom - pixi(counter, 0, -COLLECTOR_BOTTOM).y);
    }
    // Measured from the box's own place: no drop.
    this._fitLandscapeDrop = 0;
    const edgesAt = (fit) => {
      this._fitLandscapeScale = fit;
      this.applyReelBlockScale();
      skeleton.updateWorldTransform(physics);
      return {
        bottom: pixi(counter, 0, -COLLECTOR_BOTTOM).y,
        left: pixi(reels, -HOLD_AND_WIN_FRAME_HALF_WIDTH, 0).x,
        right: pixi(reels, HOLD_AND_WIN_FRAME_HALF_WIDTH, 0).x,
      };
    };
    const step = 0.1;
    const a = edgesAt(1);
    const b = edgesAt(1 + step);
    const m = PLAY_BUTTON_FIT.MARGIN_PX;
    // The counter's bottom limit: the same gap above the toolbar everywhere.
    const bottomLimit = toolbarTop - LANDSCAPE_TOOLBAR_GAP_PX - hang;
    // Each limit: the fit at which that edge reaches it.
    const solve = (at1, at2, limit) => {
      const slope = (at2 - at1) / step;
      return slope !== 0 ? 1 + (limit - at1) / slope : Infinity;
    };
    const fits = [
      // solve(a.bottom, b.bottom, toolbarTop - m),
      solve(a.bottom, b.bottom, bottomLimit),
      solve(a.left, b.left, leftLimit + m),
      solve(a.right, b.right, rightLimit - m),
    ].filter((f) => f > 0);
    const fitted = fits.length ? Math.min(...fits) : 1;
    this._fitLandscapeScale = Number.isFinite(fitted) && fitted > 0 ? fitted : 1;
    this.applyReelBlockScale();
    // ── The SAME gap to the toolbar on every screen (2026-10-14: "desktop
    // uses the space perfectly, mobiles have a couple more pixels") ── a side
    // (the jackpot column, the button column) can stop the growth before the
    // collector reaches the toolbar. The box then moves DOWN by what is left,
    // so the collector always ends MARGIN_PX above the toolbar.
    skeleton.updateWorldTransform(physics);
    // const left = toolbarTop - m - pixi(counter, 0, -COLLECTOR_BOTTOM).y;
    const left = bottomLimit - pixi(counter, 0, -COLLECTOR_BOTTOM).y;
    const pxPerUnit = Math.abs(this.spine.scale?.y || 1);
    if (left > 0.5) {
      this._fitLandscapeDrop = left / pxPerUnit;
      this.applyReelBlockScale();
    }
  }

  /** The two board slots, plus the respin counter's. */
  attachSlotContainers() {
    super.attachSlotContainers();
    if (!this.spine?.skeleton) return;
    // ── The fire trails fly ABOVE the whole room ──
    // A sibling of the spine, drawn after it — over every cell, the counter
    // and the collector (Vladimir, 2026-09-29: "fire must be ABOVE the
    // collector, mandatory"). Chest collections and the End Collector both
    // fly here.
    if (!this.trailLayer) {
      this.trailLayer = new PIXI.Container();
      this.trailLayer.name = "hold_and_win_trail_layer";
      this.trailLayer.sortableChildren = true;
      this.trailLayer.zIndex = 50;
      this.container?.addChild(this.trailLayer);
    }
    this.respinCounterSlotContainer = this._attachContainer({
      name: "hold_and_win_respin_counter_slot_content",
      registryKey: HOLD_AND_WIN_GAME_VIEW_SLOTS.RESPIN_COUNTER,
      // The new export's `attachment_spin_counter`; our old hand-added name
      // as the fallback.
      slotNames: ["attachment_spin_counter", "respin_counter_placeholder"],
    });
  }

  /** The lock plates go in with the rest of the room. */
  initializeSpine() {
    super.initializeSpine();
    this.attachLockedRows();
    // The lock plates are this room's own too (spineDormancy).
    this._syncDormancy();
  }

  _dormancySpines() {
    return [
      ...super._dormancySpines(),
      ...(this._lockedRows || []).map((row) => row.spine),
    ];
  }

  /**
   * One `locked_reel` spine per lock slot, hidden until setUnlockedRows. In a
   * holder, like the reel anticipation: spine-pixi rewrites the slot object's
   * own visible every frame, so the spine inside is shown / hidden.
   */
  attachLockedRows() {
    if (this._lockedRows || !this.spine?.skeleton) return;
    // Its page onto the GPU now (2026-10-07): the plates are built hidden, in
    // a pose that draws nothing, so it uploaded at the H&W entry.
    SpineAtlasUploader.upload("locked_reel");
    const {
      SLOT_PREFIX,
      FIRST_LOCKED_ROW,
      COUNT,
      SPINE,
      TEXT_SLOT,
      COUNT_FONT_SIZE,
      MORE_FONT_SIZE,
      MORE_KEY,
    } = HOLD_AND_WIN_LOCKED_ROWS;
    const tag = this.constructor.SCENE.TAG;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SPINE.SKELETON) || !assets.has(SPINE.ATLAS)) {
      console.warn(`[${tag}] Locked row spine not in cache`);
      return;
    }
    this._lockedRows = [];
    for (let i = 1; i <= COUNT; i++) {
      const slotName = `${SLOT_PREFIX}${i}`;
      if (!this.spine.skeleton.findSlot(slotName)) continue;
      const dataRow = FIRST_LOCKED_ROW + i - 1;
      try {
        const spine = Spine.from({
          skeleton: SPINE.SKELETON,
          atlas: SPINE.ATLAS,
        });
        spine.name = `locked_reel_${i}`;
        spine.visible = false;
        const holder = new PIXI.Container();
        holder.name = `locked_reel_${i}_slot_content`;
        holder.addChild(spine);
        this.spine.addSlotObject(slotName, holder);

        // "N MORE" — see setZoneCount. A slot object, so it fades with the
        // plate's `out` (spine-pixi copies the slot's alpha). Never
        // pixel-rounded: the spine scales it.
        const threshold = ZONE_THRESHOLDS.find(
          ([, rows]) => rows === dataRow + 1,
        )?.[0];
        // `holder` stays in the room's slot — the plate's on-screen place,
        // copied every frame once the plate lives in the overlay
        // (setPlatesHost).
        const record = { dataRow, spine, holder, threshold, count: null, more: null };
        if (threshold != null && spine.skeleton.findSlot(TEXT_SLOT)) {
          const label = new PIXI.Container();
          label.name = `locked_reel_${i}_text`;
          const makeText = (text, size) => {
            const t = new PIXI.Text(
              text,
              new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, size)),
            );
            t.anchor.set(0, 0.5);
            t.resolution = 2;
            t.roundPixels = false;
            label.addChild(t);
            return t;
          };
          record.count = makeText(String(threshold), COUNT_FONT_SIZE);
          record.more = makeText(
            i18next.t(MORE_KEY).toUpperCase(),
            MORE_FONT_SIZE,
          );
          spine.addSlotObject(TEXT_SLOT, label);
          this._layoutLockedLabel(record);
        }
        this._lockedRows.push(record);
      } catch (error) {
        console.error(`[${tag}] Failed to create locked row spine:`, error);
      }
    }
    // Rows / count set before the plates existed.
    if (this._unlockedRows != null) this.setUnlockedRows(this._unlockedRows);
    if (this._cellsInZone != null) this.setZoneCount(this._cellsInZone);
    // A host set before the plates existed takes them now.
    if (this._platesHost) this.setPlatesHost(this._platesHost);
  }

  /**
   * ── The lock plates move INTO the symbols overlay (2026-10-01) ──
   * `host` is HoldAndWinOverlay's handle: its platesLayer() sits BETWEEN the
   * cells, so the open rows' coins draw over the plates (a coin pokes up into
   * the plate above it) and the locked rows' under them (Vladimir: "i just
   * want unlocked symbols to be on top of them"). Each plate keeps its holder
   * in the room's slot and copies the holder's on-screen transform every
   * frame — the same place and size the slot gave it, through every resize.
   * `null` hands the plates back to their slots (the overlay unmounting).
   */
  setPlatesHost(host) {
    this._platesHost = host || null;
    const layer = host?.platesLayer?.() || null;
    // (this._lockedRows || []).forEach(({ spine, holder }) => {
    //   if (!spine || spine.destroyed) return;
    //   const target = layer && !layer.destroyed ? layer : holder;
    const openingLayer = host?.platesOpeningLayer?.() || null;
    (this._lockedRows || []).forEach(({ spine, holder, opening }) => {
      if (!spine || spine.destroyed) return;
      // A plate mid-`out` stays on the opening layer while the host is up.
      const home = opening && openingLayer && !openingLayer.destroyed ? openingLayer : layer;
      const target = home && !home.destroyed ? home : holder;
      if (target && !target.destroyed && spine.parent !== target) {
        target.addChild(spine);
        if (target === holder) spine.transform.setFromMatrix(new PIXI.Matrix());
      }
    });
    if (layer) {
      // ── Out BEFORE the overlay is destroyed ── @pixi/react destroys a
      // removed container's children (destroyChildren defaults to true) and
      // does it before any effect cleanup — the plates would go with it. It
      // calls willUnmount on the subtree first, so the plates go home there.
      layer.willUnmount = () => {
        if (this._platesHost === host) this.setPlatesHost(null);
      };
      // The opening layer too — a plate mid-`out` lives there.
      if (openingLayer) openingLayer.willUnmount = layer.willUnmount;
      host.setOpenRows?.(this._unlockedRows ?? HOLD_AND_WIN_LOCKED_ROWS.FIRST_LOCKED_ROW);
      if (!this._platesSync) {
        this._platesSync = () => this._syncPlates();
        PIXI.Ticker.shared.add(this._platesSync);
      }
      this._syncPlates();
    } else if (this._platesSync) {
      PIXI.Ticker.shared.remove(this._platesSync);
      this._platesSync = null;
    }
  }

  /** Every plate in the overlay onto its slot holder's on-screen place. */
  _syncPlates() {
    const layer = this._platesHost?.platesLayer?.();
    if (!layer || layer.destroyed || !layer.transform) return;
    // const toLayer = layer.worldTransform.clone().invert();
    // (this._lockedRows || []).forEach(({ spine, holder }) => {
    //   if (!spine || spine.destroyed || spine.parent !== layer) return;
    //   if (!holder || holder.destroyed || !holder.transform) return;
    //   spine.transform.setFromMatrix(toLayer.clone().append(holder.worldTransform));
    // });
    // The plates layer, and the opening layer for a plate mid-`out`.
    const opening = this._platesHost?.platesOpeningLayer?.();
    const layers = [layer];
    if (opening && !opening.destroyed && opening.transform) layers.push(opening);
    const inverse = layers.map((l) => l.worldTransform.clone().invert());
    (this._lockedRows || []).forEach(({ spine, holder }) => {
      if (!spine || spine.destroyed) return;
      const i = layers.indexOf(spine.parent);
      if (i < 0) return;
      if (!holder || holder.destroyed || !holder.transform) return;
      spine.transform.setFromMatrix(inverse[i].clone().append(holder.worldTransform));
    });
  }

  /**
   * A plate's `out` draws above every cell (2026-10-14): the clip grows the
   * plate to 1.3x as it fades, and the open row next to it came through it.
   * `on` moves it to the overlay's opening layer, off back to the plates.
   */
  _setPlateOpening(record, on) {
    // eslint-disable-next-line no-param-reassign
    record.opening = on;
    const target = on
      ? this._platesHost?.platesOpeningLayer?.()
      : this._platesHost?.platesLayer?.();
    const { spine } = record;
    if (!target || target.destroyed || !spine || spine.destroyed) return;
    if (spine.parent !== target) target.addChild(spine);
    this._syncPlates();
  }

  /** "N MORE", right-aligned at RIGHT_EDGE. */
  _layoutLockedLabel({ count, more }) {
    if (!count || !more) return;
    const { GAP, RIGHT_EDGE } = HOLD_AND_WIN_LOCKED_ROWS;
    const start = RIGHT_EDGE - (count.width + GAP + more.width);
    count.x = start;
    more.x = start + count.width + GAP;
  }

  /**
   * The active zone's occupied cells (the server's cells_in_zone, or the
   * count as the landings play): every plate shows how many more open its
   * row. Never below 0 — at 0 the row is opening.
   */
  setZoneCount(cellsInZone) {
    this._cellsInZone = cellsInZone;
    (this._lockedRows || []).forEach((record) => {
      if (!record.count || record.threshold == null) return;
      const label = String(Math.max(0, record.threshold - cellsInZone));
      if (record.count.text === label) return;
      record.count.text = label;
      this._layoutLockedLabel(record);
    });
  }

  /**
   * ── A plate's clip by its row's importance (2026-10-23) ── `idle_N` /
   * `out_N` in locked_reel.json: N = 1 the bottom locked row … 4 the top, each
   * a step hotter. The plain `idle` / `out` when the spine has no such clip.
   */
  _lockedClip(record, base) {
    const tier = record.dataRow - HOLD_AND_WIN_LOCKED_ROWS.FIRST_LOCKED_ROW + 1;
    const name = `${base}_${tier}`;
    return record.spine.skeleton.data.findAnimation(name) ? name : base;
  }

  /**
   * ── Only the next row shakes (client, 2026-10-29) ── a locked plate's loop:
   * the row that opens next (data row `unlockedRows`) shakes its padlocks
   * (`idle_N`), every row above it rests on `idle_N_still`.
   */
  _lockedIdle(record, unlockedRows) {
    const { IDLE, STILL } = HOLD_AND_WIN_LOCKED_ROWS;
    const idle = this._lockedClip(record, IDLE);
    if (record.dataRow === unlockedRows) return idle;
    const still = `${idle}_${STILL}`;
    return record.spine.skeleton.data.findAnimation(still) ? still : idle;
  }

  /**
   * The rows below opened: the plate now next in line starts shaking, on the
   * beat it was at (idle_N and idle_N_still are both 1 s), so its glow does
   * not jump against the plates above.
   */
  _shakeNextPlate(unlockedRows) {
    const record = (this._lockedRows || []).find((r) => r.dataRow === unlockedRows);
    if (!record?.spine.visible) return;
    const state = record.spine.state;
    const current = state.getCurrent(0);
    const idle = this._lockedIdle(record, unlockedRows);
    if (!current || current.animation?.name === idle) return;
    const trackTime = current.trackTime;
    const entry = state.setAnimation(0, idle, true);
    if (entry) entry.trackTime = trackTime;
  }

  /**
   * The rows past `unlockedRows` (data rows) under their plate, at once — the
   * room's first frame, a reload. A plate already locked keeps its loop.
   */
  setUnlockedRows(unlockedRows) {
    this._unlockedRows = unlockedRows;
    // The overlay's cells: open rows over the plates, locked ones under.
    this._platesHost?.setOpenRows?.(unlockedRows);
    // const { IDLE, OUT } = HOLD_AND_WIN_LOCKED_ROWS;
    const { OUT } = HOLD_AND_WIN_LOCKED_ROWS;
    // (this._lockedRows || []).forEach(({ dataRow, spine }) => {
    (this._lockedRows || []).forEach((record) => {
      const { dataRow, spine } = record;
      const state = spine.state;
      // A plate caught mid-`out` and cleared here goes home first (clearTracks
      // fires neither its complete nor its interrupt).
      const home = () => {
        if (record.opening) this._setPlateOpening(record, false);
      };
      if (dataRow >= unlockedRows) {
        const current = state.getCurrent(0)?.animation?.name;
        // if (spine.visible && current === IDLE) return;
        // Its own locked loop, by importance (_lockedClip, 2026-10-23).
        // const idle = this._lockedClip(record, IDLE);
        // ...shaking only on the row that opens next (_lockedIdle, 2026-10-29).
        const idle = this._lockedIdle(record, unlockedRows);
        if (spine.visible && current === idle) return;
        home();
        state.clearTracks();
        spine.skeleton.setToSetupPose();
        // state.setAnimation(0, IDLE, true);
        state.setAnimation(0, idle, true);
        spine.visible = true;
      } else {
        // if (state.getCurrent(0)?.animation?.name === OUT) return;
        // ...or its tier's out_N (2026-10-23, unlockRows).
        const playing = state.getCurrent(0)?.animation?.name;
        if (playing === OUT || playing?.startsWith(`${OUT}_`)) return;
        home();
        state.clearTracks();
        spine.skeleton.setToSetupPose();
        spine.visible = false;
      }
    });
  }

  /**
   * The rows from `from` to `to` (data rows) open: their plates play `out`,
   * then hide. Resolves when every one is done.
   */
  unlockRows(from, to) {
    this._unlockedRows = to;
    // // The opening rows' coins come over the plates as the plates go.
    // this._platesHost?.setOpenRows?.(to);
    // ── AFTER the plates' `out` (Vladimir, 2026-10-01: the coins under it
    // came over the plate halfway through its out) ── see the end below.
    const { OUT } = HOLD_AND_WIN_LOCKED_ROWS;
    const opening = (this._lockedRows || []).filter(
      ({ dataRow, spine }) => dataRow >= from && dataRow < to && spine.visible,
    );
    // The bowls flare as the plates start to go — once, however many rows.
    if (opening.length) this.playPotFirePulse();
    // ...and the unlock is heard, once (batch 3, 2026-10-22).
    if (opening.length) soundManager.play(SOUND_NAME.unlock);
    const plates = Promise.all(
      opening.map(
        // ({ spine }) =>
        (record) =>
          new Promise((resolve) => {
            const { spine } = record;
            const state = spine.state;
            // const entry = state.setAnimation(0, OUT, false);
            // ── Its OWN unlock, by importance (2026-10-23) ── out_1 the
            // bottom row … out_4 the top: hotter glow, bigger burst, harder
            // lock shake, longer, step by step (_lockedClip).
            const entry = state.setAnimation(0, this._lockedClip(record, OUT), false);
            if (!entry) {
              spine.visible = false;
              resolve();
              return;
            }
            // Its `out` over every cell; back on the plates layer, hidden, after.
            this._setPlateOpening(record, true);
            entry.listener = {
              complete: () => {
                // A setUnlockedRows (a new room) may have taken the plate over.
                if (state.getCurrent(0) === entry) {
                  state.clearTracks();
                  spine.skeleton.setToSetupPose();
                  spine.visible = false;
                }
                this._setPlateOpening(record, false);
                resolve();
              },
              interrupt: () => {
                this._setPlateOpening(record, false);
                resolve();
              },
            };
          }),
      ),
    );
    // The plates are gone: the opened rows' coins come up over the plates
    // still standing (unless a newer row count took over meanwhile).
    return plates.then(() => {
      if (this._unlockedRows === to) this._platesHost?.setOpenRows?.(to);
      // ...and the row now next in line starts shaking (2026-10-29).
      if (this._unlockedRows === to) this._shakeNextPlate(to);
    });
  }

  /**
   * The coin counter — `count/40` in the Royal plate's box. Built on first
   * use (Pot Collection's pattern), a slot object so the plate's bones place
   * and scale it in both layouts. Never pixel-rounded: the spine scales it.
   */
  setCoinCounter(count, total = HOLD_AND_WIN_COIN_COUNTER.TOTAL) {
    const { SLOT, FONT_SIZE, MAX_WIDTH } = HOLD_AND_WIN_COIN_COUNTER;
    if (!this.spine?.skeleton?.findSlot(SLOT)) return;

    if (!this._coinCounterText || this._coinCounterText.destroyed) {
      const container = new PIXI.Container();
      container.name = "hold_and_win_coin_counter";
      const text = new PIXI.Text(
        "",
        // new PIXI.TextStyle(scaleStyle(FONT_STYLES.winText, FONT_SIZE)),
        // The plates' golden look (fontStyles `jackpots`, 2026-09-30).
        new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, FONT_SIZE)),
      );
      text.anchor.set(0.5);
      text.resolution = 2;
      text.roundPixels = false;
      container.addChild(text);
      this.spine.addSlotObject(SLOT, container);
      this._coinCounterText = text;
    }

    const text = this._coinCounterText;
    const label = `${count}/${total}`;
    if (text.text !== label) text.text = label;
    text.scale.set(1);
    if (text.width > MAX_WIDTH) text.scale.set(MAX_WIDTH / text.width);
  }

  /**
   * A jackpot plate's value text (setJackpotValues, `txt_slot_jckpt_<tier>`)
   * — where the End Collector's Royal award flies from (GDD End Collector
   * step 6), and the size its number leaves at. Null before the plates are
   * built.
   * @param {string} tier royal | grand | major | midi | minor | mini
   */
  jackpotPlateLabel(tier) {
    const text = this._jackpotTexts?.[tier];
    return text && !text.destroyed ? text : null;
  }

  // ── The room frame during the last cells' anticipation (2026-10-07) ──
  // The animator's reel_full_in / _idle / _out (frame glow + fire around the
  // grid) on their own track, for the whole chain — the base game's board
  // frame (reel_anticipation) does the same for its chain. Its layers have no
  // setup attachment; the clips switch them on, so after the out they go back
  // to setup (a clipping mask among them must never linger).

  _gridAnticipationSlots() {
    if (this._gridAntSlots) return this._gridAntSlots;
    const data = this.spine?.skeleton?.data;
    const slots = new Set();
    for (const name of GRID_ANTICIPATION_CLIPS) {
      const anim = data?.findAnimation(name);
      for (const tl of anim?.timelines || []) {
        if (typeof tl.slotIndex === "number") slots.add(tl.slotIndex);
      }
    }
    this._gridAntSlots = [...slots];
    return this._gridAntSlots;
  }

  /** The chain has started: in → idle. Already up = left as it is. */
  playGridAnticipation() {
    const state = this.spine?.state;
    const data = this.spine?.skeleton?.data;
    if (!state || !data?.findAnimation(GRID_ANTICIPATION_IDLE)) return;
    this._gridAntOutTimer?.clear();
    this._gridAntOutTimer = null;
    const current = state.getCurrent(GRID_ANTICIPATION_TRACK)?.animation?.name;
    if (current && current !== GRID_ANTICIPATION_OUT) return;
    if (data.findAnimation(GRID_ANTICIPATION_IN)) {
      state.setAnimation(GRID_ANTICIPATION_TRACK, GRID_ANTICIPATION_IN, false);
      state.addAnimation(GRID_ANTICIPATION_TRACK, GRID_ANTICIPATION_IDLE, true, 0);
    } else {
      state.setAnimation(GRID_ANTICIPATION_TRACK, GRID_ANTICIPATION_IDLE, true);
    }
  }

  /** The chain's last cell has stopped: out, then back to nothing. */
  stopGridAnticipation() {
    const state = this.spine?.state;
    if (!state?.getCurrent(GRID_ANTICIPATION_TRACK)) return;
    const out = this.spine.skeleton.data.findAnimation(GRID_ANTICIPATION_OUT);
    if (!out) {
      this.hideGridAnticipation();
      return;
    }
    if (state.getCurrent(GRID_ANTICIPATION_TRACK).animation?.name === GRID_ANTICIPATION_OUT) return;
    state.setAnimation(GRID_ANTICIPATION_TRACK, GRID_ANTICIPATION_OUT, false);
    this._gridAntOutTimer?.clear();
    // On the clip's length, not its listener.
    this._gridAntOutTimer = new TickerTimeout(() => {
      this._gridAntOutTimer = null;
      this.hideGridAnticipation();
    }, out.duration * 1000);
  }

  // ── The Royal plate (2026-10-07, Vladimir) ── the animator's rjp_* clips on
  // their own track. `rjp_anticipation` plays WITH the last cells' chain (the
  // cells and the frame above); `rjp_anticipation_win` takes over as the 40th
  // coin lands and stays through the Royal popup, out when the collection
  // starts. Their layers have no setup attachment — back to setup after.

  _royalPlateSlots() {
    if (this._royalSlots) return this._royalSlots;
    const data = this.spine?.skeleton?.data;
    const slots = new Set();
    for (const name of ROYAL_PLATE_CLIPS) {
      for (const tl of data?.findAnimation(name)?.timelines || []) {
        if (typeof tl.slotIndex === "number") slots.add(tl.slotIndex);
      }
    }
    this._royalSlots = [...slots];
    return this._royalSlots;
  }

  _royalPlatePlay(inClip, loopClip) {
    const state = this.spine?.state;
    const data = this.spine?.skeleton?.data;
    if (!state || !data?.findAnimation(loopClip)) return;
    this._royalOutTimer?.clear();
    this._royalOutTimer = null;
    if (data.findAnimation(inClip)) {
      state.setAnimation(ROYAL_PLATE_TRACK, inClip, false);
      state.addAnimation(ROYAL_PLATE_TRACK, loopClip, true, 0);
    } else {
      state.setAnimation(ROYAL_PLATE_TRACK, loopClip, true);
    }
  }

  _royalPlateOut(outClip) {
    const state = this.spine?.state;
    if (!state?.getCurrent(ROYAL_PLATE_TRACK)) return;
    const out = this.spine.skeleton.data.findAnimation(outClip);
    if (!out) {
      this.hideRoyalPlate();
      return;
    }
    if (state.getCurrent(ROYAL_PLATE_TRACK).animation?.name === outClip) return;
    state.setAnimation(ROYAL_PLATE_TRACK, outClip, false);
    this._royalOutTimer?.clear();
    this._royalOutTimer = new TickerTimeout(() => {
      this._royalOutTimer = null;
      this.hideRoyalPlate();
    }, out.duration * 1000);
  }

  /** The last cells' chain has started: the plate glows with it. */
  playRoyalAnticipation() {
    const current = this.spine?.state?.getCurrent(ROYAL_PLATE_TRACK)?.animation?.name;
    // Already glowing (or already won) — left as it is.
    if (current && current !== ROYAL_ANTICIPATION_OUT) return;
    this._royalPlatePlay(ROYAL_ANTICIPATION_IN, ROYAL_ANTICIPATION_LOOP);
  }

  /** The chain is over without the Royal: the glow goes. */
  stopRoyalAnticipation() {
    const current = this.spine?.state?.getCurrent(ROYAL_PLATE_TRACK)?.animation?.name;
    if (current === ROYAL_WIN_IN || current === ROYAL_WIN_LOOP) return; // won: stays
    this._royalPlateOut(ROYAL_ANTICIPATION_OUT);
  }

  /** The 40th coin has landed — the Royal: its win glow, until the collection. */
  playRoyalWin() {
    const current = this.spine?.state?.getCurrent(ROYAL_PLATE_TRACK)?.animation?.name;
    if (current === ROYAL_WIN_IN || current === ROYAL_WIN_LOOP) return;
    this._royalPlatePlay(ROYAL_WIN_IN, ROYAL_WIN_LOOP);
  }

  /** The Royal popup has closed, the collection starts: the win glow goes. */
  stopRoyalWin() {
    this._royalPlateOut(ROYAL_WIN_OUT);
  }

  /** At once: nothing on the plate. */
  hideRoyalPlate() {
    this._royalOutTimer?.clear();
    this._royalOutTimer = null;
    const state = this.spine?.state;
    const skeleton = this.spine?.skeleton;
    if (!state || !skeleton) return;
    state.clearTrack(ROYAL_PLATE_TRACK);
    for (const i of this._royalPlateSlots()) skeleton.slots[i]?.setToSetupPose();
  }

  /** At once: the track cleared, its layers back to setup (hidden). */
  hideGridAnticipation() {
    this._gridAntOutTimer?.clear();
    this._gridAntOutTimer = null;
    const state = this.spine?.state;
    const skeleton = this.spine?.skeleton;
    if (!state || !skeleton) return;
    state.clearTrack(GRID_ANTICIPATION_TRACK);
    for (const i of this._gridAnticipationSlots()) skeleton.slots[i]?.setToSetupPose();
  }

  /**
   * The box zooms out to the collector's fit (REEL_ZOOM) and stays there.
   * Resolves when the clip is over (a ticker timer on its duration).
   */
  playReelZoomOut() {
    const state = this.spine?.state;
    const clip = this.spine?.skeleton?.data?.findAnimation(REEL_ZOOM.CLIP);
    if (!state || !clip) return Promise.resolve();
    this._reelZoomTimer?.clear();
    // Not looping, never cleared: the entry holds its last frame.
    state.setAnimation(REEL_ZOOM.TRACK, REEL_ZOOM.CLIP, false);
    // ── The stone grinding back (2026-10-22) ── the base game's reel spin
    // loops while the reels move back, as the reels play it.
    // soundManager.stop(SOUND_NAME.reel_spin);
    // soundManager.play(SOUND_NAME.reel_spin);
    // Its own copy, louder over the new H&W track (2026-10-29: "barely
    // audible because of our background").
    soundManager.stop(SOUND_NAME.hold_and_win_zoom_spin);
    soundManager.play(SOUND_NAME.hold_and_win_zoom_spin);
    return new Promise((resolve) => {
      this._reelZoomTimer = new TickerTimeout(() => {
        this._reelZoomTimer = null;
        // soundManager.stop(SOUND_NAME.reel_spin);
        soundManager.stop(SOUND_NAME.hold_and_win_zoom_spin);
        resolve();
      }, clip.duration * 1000);
    });
  }

  /**
   * The bowls' fire flares once (POT_FIRE_PULSE). Not while one is still
   * playing — a restart would snap the fire back to its rest size.
   */
  playPotFirePulse() {
    const state = this.spine?.state;
    if (!state || !this.spine.skeleton.data.findAnimation(POT_FIRE_PULSE.CLIP)) return;
    const current = state.getCurrent(POT_FIRE_PULSE.TRACK);
    if (current && !current.isComplete()) return;
    // Not looping, never cleared: its last frame is the rest pose.
    state.setAnimation(POT_FIRE_PULSE.TRACK, POT_FIRE_PULSE.CLIP, false);
  }

  /** At once: the box back to its respin size (the bone's setup scale). */
  resetReelZoom() {
    // Cut short mid-move: its grinding goes too.
    // if (this._reelZoomTimer) soundManager.stop(SOUND_NAME.reel_spin);
    if (this._reelZoomTimer) soundManager.stop(SOUND_NAME.hold_and_win_zoom_spin);
    this._reelZoomTimer?.clear();
    this._reelZoomTimer = null;
    this.spine?.state?.clearTrack(REEL_ZOOM.TRACK);
    const bone = this.spine?.skeleton?.findBone(REEL_ZOOM.BONE);
    if (bone) {
      bone.scaleX = bone.data.scaleX;
      bone.scaleY = bone.data.scaleY;
    }
  }

  destroy() {
    this._reelZoomTimer?.clear();
    this._reelZoomTimer = null;
    // The plates are destroyed below — awake first (spineDormancy).
    setSpinesDormant(this._dormancySpines(), false);
    this._gridAntOutTimer?.clear();
    this._gridAntOutTimer = null;
    this._royalOutTimer?.clear();
    this._royalOutTimer = null;
    if (this._platesSync) PIXI.Ticker.shared.remove(this._platesSync);
    this._platesSync = null;
    this._platesHost = null;
    this._unregisterSlot?.(HOLD_AND_WIN_GAME_VIEW_SLOTS.RESPIN_COUNTER);
    this._coinCounterText = null;
    (this._lockedRows || []).forEach(({ spine }) => {
      if (!spine.destroyed) spine.destroy();
    });
    this._lockedRows = null;
    super.destroy();
  }
}

export default HoldAndWinGameViewController;
